/**
 * Simple GEDCOM parser for family tree visualization
 */

/**
 * Parse GEDCOM file content into structured data
 * @param {string} gedcomContent - Raw GEDCOM file content
 * @returns {Object} Parsed family tree data
 */
export const parseGedcomToTree = (gedcomContent) => {
  try {
    const lines = gedcomContent.split('\n').map(line => line.trim()).filter(line => line);
    const individuals = {};
    const families = {};
    
    let currentRecord = null;
    let currentSubRecord = null;
    
    for (const line of lines) {
      const parts = line.split(' ');
      const level = parseInt(parts[0]);
      const tag = parts[1];
      const data = parts.slice(2).join(' ');
      
      if (level === 0) {
        // New top-level record
        if (tag.startsWith('@') && tag.endsWith('@')) {
          // This is a pointer/ID
          const id = tag;
          const recordType = parts[2];
          
          if (recordType === 'INDI') {
            currentRecord = {
              id: id,
              type: 'INDI',
              name: '',
              birth: '',
              death: '',
              birthPlace: '',
              deathPlace: '',
              sex: '',
              families: [],
              parents: null
            };
            individuals[id] = currentRecord;
          } else if (recordType === 'FAM') {
            currentRecord = {
              id: id,
              type: 'FAM',
              husband: null,
              wife: null,
              children: [],
              marriage: ''
            };
            families[id] = currentRecord;
          } else {
            currentRecord = null;
          }
        }
        currentSubRecord = null;
      } else if (level === 1 && currentRecord) {
        // First level under a record
        currentSubRecord = { tag, data };
        
        switch (tag) {
          case 'NAME':
            if (currentRecord.type === 'INDI') {
              currentRecord.name = data.replace(/\//g, '');
            }
            break;
          case 'SEX':
            if (currentRecord.type === 'INDI') {
              currentRecord.sex = data;
            }
            break;
          case 'FAMC':
            if (currentRecord.type === 'INDI') {
              currentRecord.parents = data;
            }
            break;
          case 'FAMS':
            if (currentRecord.type === 'INDI') {
              currentRecord.families.push(data);
            }
            break;
          case 'HUSB':
            if (currentRecord.type === 'FAM') {
              currentRecord.husband = data;
            }
            break;
          case 'WIFE':
            if (currentRecord.type === 'FAM') {
              currentRecord.wife = data;
            }
            break;
          case 'CHIL':
            if (currentRecord.type === 'FAM') {
              currentRecord.children.push(data);
            }
            break;
          default:
            // Other tags are ignored for now
            break;
        }
      } else if (level === 2 && currentSubRecord) {
        // Second level - usually dates and places
        if (tag === 'DATE') {
          if (currentSubRecord.tag === 'BIRT' && currentRecord.type === 'INDI') {
            currentRecord.birth = data;
          } else if (currentSubRecord.tag === 'DEAT' && currentRecord.type === 'INDI') {
            currentRecord.death = data;
          } else if (currentSubRecord.tag === 'MARR' && currentRecord.type === 'FAM') {
            currentRecord.marriage = data;
          }
        } else if (tag === 'PLAC') {
          if (currentSubRecord.tag === 'BIRT' && currentRecord.type === 'INDI') {
            currentRecord.birthPlace = data;
          } else if (currentSubRecord.tag === 'DEAT' && currentRecord.type === 'INDI') {
            currentRecord.deathPlace = data;
          }
        }
      }
    }
    
    return { individuals, families };
  } catch (error) {
    console.error('Error parsing GEDCOM:', error);
    return { individuals: {}, families: {} };
  }
};

/**
 * Convert parsed GEDCOM data to D3 tree format
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} rootPersonId - ID of root person for tree
 * @returns {Object} D3-compatible tree structure
 */
export const convertToD3Tree = (gedcomData, rootPersonId) => {
  const { individuals, families } = gedcomData;
  
  if (!individuals[rootPersonId]) {
    console.error('Root person not found:', rootPersonId);
    return null;
  }
  
  const buildNode = (personId, visited = new Set()) => {
    if (visited.has(personId) || !individuals[personId]) {
      return null;
    }
    
    visited.add(personId);
    const person = individuals[personId];
    
    const node = {
      name: person.name || 'Unknown',
      attributes: {
        birth: person.birth,
        death: person.death,
        sex: person.sex,
        id: personId
      },
      children: []
    };
    
    // Add children from all families where this person is a parent
    person.families.forEach(familyId => {
      const family = families[familyId];
      if (family && (family.husband === personId || family.wife === personId)) {
        family.children.forEach(childId => {
          const childNode = buildNode(childId, new Set(visited));
          if (childNode) {
            node.children.push(childNode);
          }
        });
      }
    });
    
    return node;
  };
  
  return buildNode(rootPersonId);
};

/**
 * Find potential root persons (those without parents or oldest generation)
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @returns {Array} Array of potential root persons
 */
export const findRootPersons = (gedcomData) => {
  const { individuals } = gedcomData;
  
  const rootCandidates = Object.values(individuals)
    .filter(person => !person.parents || person.parents === '')
    .map(person => ({
      id: person.id,
      name: person.name || 'Unknown',
      birth: person.birth
    }))
    .sort((a, b) => {
      // Sort by birth date if available, otherwise by name
      if (a.birth && b.birth) {
        return a.birth.localeCompare(b.birth);
      }
      return a.name.localeCompare(b.name);
    });
  
  // If no clear roots found, return all individuals
  if (rootCandidates.length === 0) {
    return Object.values(individuals)
      .slice(0, 10) // Limit to first 10 to avoid overwhelming
      .map(person => ({
        id: person.id,
        name: person.name || 'Unknown',
        birth: person.birth
      }));
  }
  
  return rootCandidates;
};

/**
 * Search for persons across all data fields (names, dates, locations)
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} searchTerm - Term to search for across all fields
 * @returns {Array} Array of matching persons with match details
 */
export const searchPersons = (gedcomData, searchTerm) => {
  const { individuals } = gedcomData;
  
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const term = searchTerm.toLowerCase().trim();
  
  return Object.values(individuals)
    .map(person => {
      const matches = [];
      let relevanceScore = 0;
      
      // Search in name (highest priority)
      if (person.name && person.name.toLowerCase().includes(term)) {
        matches.push('name');
        relevanceScore += 10;
        // Exact name match gets higher score
        if (person.name.toLowerCase() === term) {
          relevanceScore += 20;
        }
        // Name starts with term gets higher score
        if (person.name.toLowerCase().startsWith(term)) {
          relevanceScore += 5;
        }
      }
      
      // Search in birth date
      if (person.birth && person.birth.toLowerCase().includes(term)) {
        matches.push('birth');
        relevanceScore += 3;
      }
      
      // Search in death date
      if (person.death && person.death.toLowerCase().includes(term)) {
        matches.push('death');
        relevanceScore += 3;
      }
      
      // Search in birth place
      if (person.birthPlace && person.birthPlace.toLowerCase().includes(term)) {
        matches.push('birthPlace');
        relevanceScore += 5;
      }
      
      // Search in death place
      if (person.deathPlace && person.deathPlace.toLowerCase().includes(term)) {
        matches.push('deathPlace');
        relevanceScore += 5;
      }
      
      // Return person with match info if any matches found
      if (matches.length > 0) {
        return {
          id: person.id,
          name: person.name || 'Unknown',
          birth: person.birth,
          death: person.death,
          birthPlace: person.birthPlace,
          deathPlace: person.deathPlace,
          matches,
          relevanceScore
        };
      }
      
      return null;
    })
    .filter(Boolean) // Remove null entries
    .sort((a, b) => {
      // Sort by relevance score first, then by name
      if (b.relevanceScore !== a.relevanceScore) {
        return b.relevanceScore - a.relevanceScore;
      }
      return a.name.localeCompare(b.name);
    })
    .slice(0, 20); // Limit results
};

/**
 * Get detailed information about a person
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} personId - ID of the person
 * @returns {Object} Detailed person information
 */
export const getPersonDetails = (gedcomData, personId) => {
  const { individuals } = gedcomData;
  const person = individuals[personId];
  
  if (!person) return null;
  
  return {
    ...person,
    id: personId
  };
};

/**
 * Find relationship path between two people using breadth-first search
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} person1Id - ID of first person
 * @param {string} person2Id - ID of second person
 * @returns {Array|null} Array of persons in the relationship path
 */
export const findRelationshipPath = (gedcomData, person1Id, person2Id) => {
  const { individuals, families } = gedcomData;
  
  if (person1Id === person2Id) {
    return [individuals[person1Id]];
  }
  
  // Build adjacency list for family relationships
  const connections = {};
  
  // Initialize connections
  Object.keys(individuals).forEach(id => {
    connections[id] = new Set();
  });
  
  // Add family connections
  Object.values(families).forEach(family => {
    const members = [];
    
    // Add parents
    if (family.husband) members.push(family.husband);
    if (family.wife) members.push(family.wife);
    
    // Add children
    if (family.children) {
      members.push(...family.children);
    }
    
    // Connect all family members to each other
    for (let i = 0; i < members.length; i++) {
      for (let j = i + 1; j < members.length; j++) {
        if (connections[members[i]] && connections[members[j]]) {
          connections[members[i]].add(members[j]);
          connections[members[j]].add(members[i]);
        }
      }
    }
  });
  
  // Breadth-first search to find shortest path
  const queue = [[person1Id]];
  const visited = new Set([person1Id]);
  
  while (queue.length > 0) {
    const path = queue.shift();
    const currentId = path[path.length - 1];
    
    if (currentId === person2Id) {
      // Found the target, return the path with person objects
      return path.map(id => individuals[id]).filter(Boolean);
    }
    
    // Explore connected persons
    if (connections[currentId]) {
      for (const connectedId of connections[currentId]) {
        if (!visited.has(connectedId)) {
          visited.add(connectedId);
          queue.push([...path, connectedId]);
        }
      }
    }
  }
  
  return null; // No path found
};

/**
 * Get search suggestions when no results are found
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} searchTerm - Original search term
 * @returns {Array} Array of suggested search terms
 */
export const getSearchSuggestions = (gedcomData, searchTerm) => {
  const { individuals } = gedcomData;
  
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const suggestions = new Set();
  const term = searchTerm.toLowerCase().trim();
  
  // Get common names, places, and dates for suggestions
  Object.values(individuals).forEach(person => {
    // Suggest similar names
    if (person.name) {
      const nameParts = person.name.toLowerCase().split(' ');
      nameParts.forEach(part => {
        if (part.length > 2 && part.includes(term.charAt(0))) {
          suggestions.add(part);
        }
      });
    }
    
    // Suggest locations
    [person.birthPlace, person.deathPlace].forEach(place => {
      if (place) {
        const placeParts = place.toLowerCase().split(',').map(p => p.trim());
        placeParts.forEach(part => {
          if (part.length > 2 && part.includes(term.charAt(0))) {
            suggestions.add(part);
          }
        });
      }
    });
  });
  
  // Convert to array and limit suggestions
  return Array.from(suggestions)
    .filter(suggestion => suggestion !== term)
    .sort()
    .slice(0, 5);
};

/**
 * Clear all search and filter states
 * @param {Object} filters - Current filter state
 * @returns {Object} Reset filter state
 */
export const clearAllFilters = (filters) => {
  return {
    generation: 'all',
    gender: 'all',
    location: 'all',
    dateRange: 'all'
  };
};
export const getEnhancedFamilyStats = (gedcomData) => {
  const { individuals, families } = gedcomData;
  
  const stats = {
    totalPeople: Object.keys(individuals).length,
    totalFamilies: Object.keys(families).length,
    generations: {},
    locations: {},
    surnames: {},
    livingCount: 0,
    deceasedCount: 0,
    averageAge: 0,
    oldestPerson: null,
    youngestPerson: null
  };
  
  let totalAge = 0;
  let ageCount = 0;
  let oldestAge = 0;
  let youngestAge = Infinity;
  
  Object.values(individuals).forEach(person => {
    // Count living vs deceased
    if (person.death) {
      stats.deceasedCount++;
    } else {
      stats.livingCount++;
    }
    
    // Calculate ages
    if (person.birth) {
      const birthYear = parseInt(person.birth.match(/\d{4}/)?.[0]);
      if (birthYear) {
        const currentYear = person.death ? 
          parseInt(person.death.match(/\d{4}/)?.[0]) : 
          new Date().getFullYear();
        
        if (currentYear) {
          const age = currentYear - birthYear;
          if (age > 0 && age < 150) { // Reasonable age range
            totalAge += age;
            ageCount++;
            
            if (age > oldestAge) {
              oldestAge = age;
              stats.oldestPerson = person;
            }
            
            if (age < youngestAge && person.death) { // Only for deceased
              youngestAge = age;
              stats.youngestPerson = person;
            }
          }
        }
        
        // Generation analysis
        const decade = Math.floor(birthYear / 10) * 10;
        stats.generations[decade] = (stats.generations[decade] || 0) + 1;
      }
    }
    
    // Location analysis
    [person.birthPlace, person.deathPlace].forEach(place => {
      if (place) {
        const location = place.split(',').pop().trim();
        stats.locations[location] = (stats.locations[location] || 0) + 1;
      }
    });
    
    // Surname analysis
    if (person.name) {
      const surname = person.name.split(' ').pop();
      stats.surnames[surname] = (stats.surnames[surname] || 0) + 1;
    }
  });
  
  stats.averageAge = ageCount > 0 ? Math.round(totalAge / ageCount) : 0;
  
  return stats;
};
